import React, { useCallback, useEffect, useRef } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { useGetFullSubscriptionDetailsQuery } from '@features/subscriptions/store/api';
import { formatPrice, getMonthlyAmountCents } from '@features/subscriptions/utils';
import { useOrganizationInfo, useUserRoles } from '@features/user';
import { Box, Card, CircularProgress } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks';
import { ROLES } from '@utils/roleUtils';

import { useJotMeetAvailability } from '../../hooks/useJotMeetAvailability';
import { useTelehealthAccess } from '../../hooks/useTelehealthAccess';
import { useGetProviderWaitingRoomLinkQuery } from '../../waiting-room/store';

import { JotMeetActiveView } from './JotMeetActiveView';
import { JotMeetHeader } from './JotMeetHeader';
import { JotMeetMarketingView } from './JotMeetMarketingView';

interface JotMeetSettingsSectionProps {
  /** Callback to navigate to the Clinician settings section (where the communications name lives). */
  onOpenClinicianSettings: () => void;
  /** Toast handler used by the Copy button. Provided by the host so we don't depend on notistack here. */
  onCopySuccess: () => void;
  onCopyError: () => void;
}

/**
 * Container for the JotMeet settings section.
 *
 * Decides which view to render based on bundle catalog + entitlement:
 *   - JotMeet not in catalog → nothing (caller should also hide the nav item)
 *   - Catalog loading → spinner
 *   - Entitled → active view (waiting-room link + how-to)
 *   - Not entitled → marketing view (feature cards + role-conditional CTA)
 */
export const JotMeetSettingsSection: React.FC<JotMeetSettingsSectionProps> = ({
  onOpenClinicianSettings,
  onCopySuccess,
  onCopyError,
}) => {
  const { addon, isAvailable, isLoading: isCatalogLoading } = useJotMeetAvailability();
  const { hasAccess: hasTelehealthEntitlement, isLoading: isEntitlementLoading } =
    useTelehealthAccess();

  const roles = useUserRoles();
  const orgInfo = useOrganizationInfo();
  const isPracticeAdmin = roles.includes(ROLES.PRACTICE_ADMINISTRATOR);
  const isGroupAdmin = isPracticeAdmin && (orgInfo?.group ?? false);

  // Pricing for the marketing CTA — derived from the addon bundle directly.
  const { data: subscriptionDetails } = useGetFullSubscriptionDetailsQuery(undefined, {
    skip: !isAvailable,
  });
  const isAnnual = subscriptionDetails?.billing_interval === 'year';
  const addonPriceCents = addon ? getMonthlyAmountCents(addon, isAnnual) : 0;
  const priceLabel = addon ? formatPrice(addonPriceCents, addon.currency ?? 'usd') : null;

  // Waiting-room link — only fetched when entitled (RTK Query skips otherwise).
  const {
    data: waitingRoomLink,
    isLoading: isWaitingRoomLinkLoading,
    isFetching: isWaitingRoomLinkFetching,
    error: waitingRoomLinkError,
    refetch: refetchWaitingRoomLink,
  } = useGetProviderWaitingRoomLinkQuery(undefined, {
    skip: !hasTelehealthEntitlement,
  });

  const { open: openModal } = useModal();
  const { jotmeetSettings } = useEventTracking();

  const handleLearnMore = useCallback(() => {
    jotmeetSettings.learnMoreClicked();
    openModal(MODAL_TYPES.ADDON_ONBOARDING, {
      addonId: 'jotmeet',
      sx: { borderRadius: 3, overflow: 'hidden' },
    });
  }, [jotmeetSettings, openModal]);

  const handleNonAdminLearnMore = useCallback(() => {
    jotmeetSettings.nonAdminLearnMoreClicked();
    openModal(MODAL_TYPES.ADDON_ONBOARDING, {
      addonId: 'jotmeet',
      sx: { borderRadius: 3, overflow: 'hidden' },
    });
  }, [jotmeetSettings, openModal]);

  const handleCopyLink = useCallback(async () => {
    if (!waitingRoomLink?.short_url) {
      jotmeetSettings.waitingRoomLinkCopyAttempted({
        outcome: 'unavailable',
        has_link: false,
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(waitingRoomLink.short_url);
      jotmeetSettings.waitingRoomLinkCopyAttempted({
        outcome: 'success',
        has_link: true,
      });
      jotmeetSettings.waitingRoomLinkCopied();
      onCopySuccess();
    } catch {
      jotmeetSettings.waitingRoomLinkCopyAttempted({
        outcome: 'failed',
        has_link: true,
      });
      onCopyError();
    }
  }, [waitingRoomLink, jotmeetSettings, onCopySuccess, onCopyError]);

  // Fire the view event once per state — re-fires only when the state changes.
  const lastTrackedStateRef = useRef<string | null>(null);
  useEffect(() => {
    if (isCatalogLoading || !isAvailable) return;
    if (hasTelehealthEntitlement && isWaitingRoomLinkLoading) return;
    const state = hasTelehealthEntitlement
      ? isPracticeAdmin
        ? 'entitled-admin'
        : 'entitled-non-admin'
      : isPracticeAdmin
        ? 'unentitled-admin'
        : 'unentitled-non-admin';
    if (lastTrackedStateRef.current === state) return;
    lastTrackedStateRef.current = state;
    jotmeetSettings.opened({ settings_state: state });
  }, [
    isCatalogLoading,
    isAvailable,
    hasTelehealthEntitlement,
    isWaitingRoomLinkLoading,
    isPracticeAdmin,
    jotmeetSettings,
  ]);

  // Catalog hasn't resolved yet — show a spinner inside the card chrome.
  if (isCatalogLoading || isEntitlementLoading) {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <JotMeetHeader />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  // Defensive: if the addon isn't in the catalog, render nothing.
  // The nav item is gated on the same signal upstream, so this should be
  // unreachable — but keeps the component robust if a caller forgets.
  if (!isAvailable) return null;

  return (
    <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
      <JotMeetHeader />

      {hasTelehealthEntitlement ? (
        <JotMeetActiveView
          isPracticeAdmin={isPracticeAdmin}
          waitingRoomUrl={waitingRoomLink?.short_url ?? null}
          displayName={waitingRoomLink?.display_name ?? null}
          isLoadingLink={isWaitingRoomLinkLoading}
          isRefetchingLink={isWaitingRoomLinkFetching && !isWaitingRoomLinkLoading}
          linkError={!!waitingRoomLinkError}
          onCopyLink={() => void handleCopyLink()}
          onRetryLink={() => void refetchWaitingRoomLink()}
          onUpdateCommunicationsName={onOpenClinicianSettings}
        />
      ) : (
        <JotMeetMarketingView
          priceLabel={priceLabel}
          isGroupAdmin={isGroupAdmin}
          isPracticeAdmin={isPracticeAdmin}
          onLearnMore={handleLearnMore}
          onNonAdminLearnMore={handleNonAdminLearnMore}
        />
      )}
    </Card>
  );
};
